import React, { useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useChatter } from '../hooks/useChatter'
import ChatterMessage from './ChatterMessage'
import MessageInput from './MessageInput'
import '../styles/chatter.css'

const OpportunityChatter: React.FC = () => {
  const { opportunityId } = useParams<{ opportunityId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  
  const {
    messages,
    businessName,
    loading,
    error,
    sendMessage,
    sending
  } = useChatter({
    opportunityId: opportunityId!,
    autoMarkAsRead: true,
    enableRealTime: true
  })

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Determine where to go back based on the referrer or state
  const handleBackClick = () => {
    // Check if we have state indicating where we came from
    const fromState = location.state?.from
    
    if (fromState) {
      // If we have explicit state about where we came from, go there
      navigate(fromState)
    } else {
      // Check the current path to make an educated guess
      const currentPath = location.pathname
      const referrer = document.referrer
      
      // If the referrer contains iso-dashboard or we don't have a clear referrer,
      // assume they came from the deals list and go back to dashboard
      if (referrer.includes('iso-dashboard') || !referrer || referrer === window.location.origin + '/') {
        navigate('/iso-dashboard')
      } else if (referrer.includes('chatter')) {
        // If they came from chatter, go back to chatter list
        navigate('/chatter')
      } else {
        // Default fallback - go to chatter list
        navigate('/chatter')
      }
    }
  }

  const getBackButtonText = () => {
    const fromState = location.state?.from
    
    if (fromState === '/iso-dashboard') {
      return '← Back to Deals'
    } else if (fromState === '/chatter') {
      return '← Back to Chatter'
    } else {
      // Default based on referrer or fallback
      const referrer = document.referrer
      if (referrer.includes('iso-dashboard')) {
        return '← Back to Deals'
      } else {
        return '← Back to Chatter'
      }
    }
  }

  if (!opportunityId) {
    return (
      <div className="chatter-error">
        <h2>Invalid Opportunity</h2>
        <p>No opportunity ID provided.</p>
        <button onClick={handleBackClick} className="btn btn-primary">
          {getBackButtonText()}
        </button>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="chatter-loading">
        <div className="loading-spinner"></div>
        <p>Loading conversation...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="chatter-error">
        <h2>Error Loading Conversation</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="btn btn-primary">
          Retry
        </button>
        <button onClick={handleBackClick} className="btn btn-secondary">
          {getBackButtonText()}
        </button>
      </div>
    )
  }

  return (
    <div className="opportunity-chatter">
      {/* Header */}
      <div className="chatter-header">
        <div className="chatter-title">
          <h1>{businessName || 'Opportunity Chat'}</h1>
          <span className="opportunity-id">ID: {opportunityId}</span>
        </div>
        <div className="chatter-stats">
          <span className="message-count">{messages.length} messages</span>
        </div>
      </div>

      {/* Messages Container */}
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="no-messages">
            <div className="no-messages-icon">💬</div>
            <h3>No messages yet</h3>
            <p>Start the conversation by sending a message below.</p>
          </div>
        ) : (
          <div className="messages-list">
            {messages.map((message) => (
              <ChatterMessage
                key={message.id}
                message={message}
                isCurrentUser={message.senderType === 'iso'}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input */}
      <div className="message-input-section">
        <MessageInput
          onSendMessage={sendMessage}
          disabled={sending}
          placeholder="Type your message..."
        />
      </div>
    </div>
  )
}

export default OpportunityChatter
